import {
  createImageEditTransform,
  projectSourcePointToStage,
  unprojectStagePointToSource,
} from './imageEditorTransform'
import { isIdentityImageAppearance } from './imageEditorAppearance'
import type { ImageFilterPreset } from './imageEditorAppearance'

export type ImageDimensions = Readonly<{
  width: number
  height: number
}>

export type NormalizedCrop = Readonly<{
  x: number
  y: number
  width: number
  height: number
}>

export type PixelCropGeometry = Readonly<{
  left: number
  top: number
  width: number
  height: number
}>

export type ImageRotation = 0 | 90 | 180 | 270

export type CropHandle =
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left'
  | 'top-left'

export type ImageEdit = Readonly<{
  crop: NormalizedCrop
  rotation: ImageRotation
  straightening: number
  flipHorizontal: boolean
  flipVertical: boolean
  brightness: number
  contrast: number
  saturation: number
  warmth: number
  filterPreset: ImageFilterPreset
  filterIntensity: number
}>

export type NormalizedImagePoint = Readonly<{
  x: number
  y: number
}>

export type ImageEditPointProjection =
  | Readonly<{
      status: 'inside'
      point: NormalizedImagePoint
    }>
  | Readonly<{
      status: 'outside'
    }>

export type ResizeCropOptions = Readonly<{
  minimumSize?: Readonly<{
    width: number
    height: number
  }>
  aspectRatio?: Readonly<{
    ratio: number
    imageDimensions: ImageDimensions
  }>
  aspectRatioRange?: Readonly<{
    min: number
    max: number
    imageDimensions: ImageDimensions
  }>
}>

const FULL_IMAGE_CROP: NormalizedCrop = {
  x: 0,
  y: 0,
  width: 1,
  height: 1,
}

const DEFAULT_MINIMUM_CROP_SIZE = 0.01
const COMPARISON_TOLERANCE = 0.000_001

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(maximum, Math.max(minimum, value))

const roundPixelValue = (value: number): number =>
  Math.round(value + Number.EPSILON * Math.max(1, Math.abs(value)) * 4)

const areClose = (first: number, second: number): boolean =>
  Math.abs(first - second) <= COMPARISON_TOLERANCE

const clampProjectedCoordinate = (value: number): number => {
  const clampedValue = clamp(value, 0, 1)
  if (areClose(clampedValue, 0)) return 0
  if (areClose(clampedValue, 1)) return 1
  return clampedValue
}

const assertFiniteNumber = (value: number, name: string): void => {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite`)
  }
}

const assertPositiveNumber = (value: number, name: string): void => {
  assertFiniteNumber(value, name)

  if (value <= 0) {
    throw new RangeError(`${name} must be greater than zero`)
  }
}

const assertImageDimensions = (
  dimensions: ImageDimensions,
  name: string,
): void => {
  assertPositiveNumber(dimensions.width, `${name}.width`)
  assertPositiveNumber(dimensions.height, `${name}.height`)
}

const assertNormalizedCrop = (crop: NormalizedCrop): void => {
  assertFiniteNumber(crop.x, 'crop.x')
  assertFiniteNumber(crop.y, 'crop.y')
  assertPositiveNumber(crop.width, 'crop.width')
  assertPositiveNumber(crop.height, 'crop.height')

  if (
    crop.x < -COMPARISON_TOLERANCE ||
    crop.y < -COMPARISON_TOLERANCE ||
    crop.x + crop.width > 1 + COMPARISON_TOLERANCE ||
    crop.y + crop.height > 1 + COMPARISON_TOLERANCE
  ) {
    throw new RangeError('crop must remain within the normalized image bounds')
  }
}

const normalizeImagePoint = (
  point: NormalizedImagePoint,
  name: string,
): NormalizedImagePoint => {
  assertFiniteNumber(point.x, `${name}.x`)
  assertFiniteNumber(point.y, `${name}.y`)

  if (
    point.x < -COMPARISON_TOLERANCE ||
    point.x > 1 + COMPARISON_TOLERANCE ||
    point.y < -COMPARISON_TOLERANCE ||
    point.y > 1 + COMPARISON_TOLERANCE
  ) {
    throw new RangeError(`${name} must remain within the normalized bounds`)
  }

  return {
    x: clamp(point.x, 0, 1),
    y: clamp(point.y, 0, 1),
  }
}

const getMinimumCropSize = (
  minimumSize: number | undefined,
  name = 'minimumSize',
): number => {
  const resolvedMinimumSize = minimumSize ?? DEFAULT_MINIMUM_CROP_SIZE
  assertPositiveNumber(resolvedMinimumSize, name)

  if (resolvedMinimumSize > 1) {
    throw new RangeError(`${name} must not exceed one`)
  }

  return resolvedMinimumSize
}

const horizontalDirectionForHandle = (handle: CropHandle): -1 | 0 | 1 => {
  if (handle === 'left' || handle.endsWith('-left')) return -1
  if (handle === 'right' || handle.endsWith('-right')) return 1
  return 0
}

const verticalDirectionForHandle = (handle: CropHandle): -1 | 0 | 1 => {
  if (handle === 'top' || handle.startsWith('top-')) return -1
  if (handle === 'bottom' || handle.startsWith('bottom-')) return 1
  return 0
}

const getAnchoredPosition = (
  anchor: number,
  size: number,
  direction: -1 | 0 | 1,
): number => {
  if (direction === -1) return anchor - size
  if (direction === 1) return anchor
  return anchor - size / 2
}

const getMaximumAnchoredSize = (
  anchor: number,
  direction: -1 | 0 | 1,
): number => {
  if (direction === -1) return anchor
  if (direction === 1) return 1 - anchor
  return 2 * Math.min(anchor, 1 - anchor)
}

const getAnchor = (
  start: number,
  size: number,
  direction: -1 | 0 | 1,
): number => {
  if (direction === -1) return start + size
  if (direction === 1) return start
  return start + size / 2
}

const getClockwiseRotation = (rotation: ImageRotation): ImageRotation => {
  switch (rotation) {
    case 0:
      return 90
    case 90:
      return 180
    case 180:
      return 270
    case 270:
      return 0
  }
}

const getCounterclockwiseRotation = (
  rotation: ImageRotation,
): ImageRotation => {
  switch (rotation) {
    case 0:
      return 270
    case 90:
      return 0
    case 180:
      return 90
    case 270:
      return 180
  }
}

const resizeCropWithoutAspectRatio = (
  crop: NormalizedCrop,
  horizontalDirection: -1 | 0 | 1,
  verticalDirection: -1 | 0 | 1,
  deltaX: number,
  deltaY: number,
  minimumWidth: number,
  minimumHeight: number,
): NormalizedCrop => {
  const originalRight = crop.x + crop.width
  const originalBottom = crop.y + crop.height
  const leftMinimumWidth = Math.min(minimumWidth, originalRight)
  const rightMinimumWidth = Math.min(minimumWidth, 1 - crop.x)
  const topMinimumHeight = Math.min(minimumHeight, originalBottom)
  const bottomMinimumHeight = Math.min(minimumHeight, 1 - crop.y)

  const left =
    horizontalDirection === -1
      ? clamp(crop.x + deltaX, 0, originalRight - leftMinimumWidth)
      : crop.x
  const right =
    horizontalDirection === 1
      ? clamp(originalRight + deltaX, crop.x + rightMinimumWidth, 1)
      : originalRight
  const top =
    verticalDirection === -1
      ? clamp(crop.y + deltaY, 0, originalBottom - topMinimumHeight)
      : crop.y
  const bottom =
    verticalDirection === 1
      ? clamp(originalBottom + deltaY, crop.y + bottomMinimumHeight, 1)
      : originalBottom

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  }
}

const resizeCropWithAspectRatio = (
  crop: NormalizedCrop,
  horizontalDirection: -1 | 0 | 1,
  verticalDirection: -1 | 0 | 1,
  deltaX: number,
  deltaY: number,
  minimumWidth: number,
  minimumHeight: number,
  aspectRatio: NonNullable<ResizeCropOptions['aspectRatio']>,
): NormalizedCrop => {
  assertPositiveNumber(aspectRatio.ratio, 'aspectRatio.ratio')
  assertImageDimensions(
    aspectRatio.imageDimensions,
    'aspectRatio.imageDimensions',
  )

  const normalizedRatio =
    (aspectRatio.ratio * aspectRatio.imageDimensions.height) /
    aspectRatio.imageDimensions.width
  const anchorX = getAnchor(crop.x, crop.width, horizontalDirection)
  const anchorY = getAnchor(crop.y, crop.height, verticalDirection)
  const proposedWidth =
    horizontalDirection === -1
      ? anchorX - (crop.x + deltaX)
      : horizontalDirection === 1
        ? crop.x + crop.width + deltaX - anchorX
        : crop.width
  const proposedHeight =
    verticalDirection === -1
      ? anchorY - (crop.y + deltaY)
      : verticalDirection === 1
        ? crop.y + crop.height + deltaY - anchorY
        : crop.height

  let desiredWidth: number

  if (horizontalDirection === 0) {
    desiredWidth = proposedHeight * normalizedRatio
  } else if (verticalDirection === 0) {
    desiredWidth = proposedWidth
  } else {
    const projectedHeight =
      (normalizedRatio * proposedWidth + proposedHeight) /
      (normalizedRatio * normalizedRatio + 1)
    desiredWidth = projectedHeight * normalizedRatio
  }

  const maximumWidth = Math.min(
    getMaximumAnchoredSize(anchorX, horizontalDirection),
    getMaximumAnchoredSize(anchorY, verticalDirection) * normalizedRatio,
  )
  const requestedMinimumWidth = Math.max(
    minimumWidth,
    minimumHeight * normalizedRatio,
  )
  const effectiveMinimumWidth = Math.min(requestedMinimumWidth, maximumWidth)
  const width = clamp(desiredWidth, effectiveMinimumWidth, maximumWidth)
  const height = width / normalizedRatio

  return {
    x: getAnchoredPosition(anchorX, width, horizontalDirection),
    y: getAnchoredPosition(anchorY, height, verticalDirection),
    width,
    height,
  }
}

export const createIdentityImageEdit = (): ImageEdit => ({
  crop: { ...FULL_IMAGE_CROP },
  rotation: 0,
  straightening: 0,
  flipHorizontal: false,
  flipVertical: false,
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
  filterPreset: 'none',
  filterIntensity: 100,
})

export const isIdentityImageEdit = (edit: ImageEdit): boolean =>
  areClose(edit.crop.x, FULL_IMAGE_CROP.x) &&
  areClose(edit.crop.y, FULL_IMAGE_CROP.y) &&
  areClose(edit.crop.width, FULL_IMAGE_CROP.width) &&
  areClose(edit.crop.height, FULL_IMAGE_CROP.height) &&
  edit.rotation === 0 &&
  edit.straightening === 0 &&
  !edit.flipHorizontal &&
  !edit.flipVertical &&
  isIdentityImageAppearance(edit)

export const projectSourcePointToCrop = (
  sourcePoint: NormalizedImagePoint,
  edit: ImageEdit,
  sourceDimensions: ImageDimensions,
): ImageEditPointProjection => {
  assertNormalizedCrop(edit.crop)
  const transform = createImageEditTransform(sourceDimensions, edit)
  const orientedPoint = projectSourcePointToStage(
    normalizeImagePoint(sourcePoint, 'sourcePoint'),
    transform,
  )
  const right = edit.crop.x + edit.crop.width
  const bottom = edit.crop.y + edit.crop.height
  const isInsideCrop =
    orientedPoint.x >= edit.crop.x - COMPARISON_TOLERANCE &&
    orientedPoint.x <= right + COMPARISON_TOLERANCE &&
    orientedPoint.y >= edit.crop.y - COMPARISON_TOLERANCE &&
    orientedPoint.y <= bottom + COMPARISON_TOLERANCE

  if (!isInsideCrop) return { status: 'outside' }

  return {
    status: 'inside',
    point: {
      x: clampProjectedCoordinate(
        (orientedPoint.x - edit.crop.x) / edit.crop.width,
      ),
      y: clampProjectedCoordinate(
        (orientedPoint.y - edit.crop.y) / edit.crop.height,
      ),
    },
  }
}

export const unprojectCropPointToSource = (
  cropPoint: NormalizedImagePoint,
  edit: ImageEdit,
  sourceDimensions: ImageDimensions,
): NormalizedImagePoint => {
  assertNormalizedCrop(edit.crop)
  const point = normalizeImagePoint(cropPoint, 'cropPoint')
  const croppedPoint = {
    x: edit.crop.x + point.x * edit.crop.width,
    y: edit.crop.y + point.y * edit.crop.height,
  }
  return normalizeImagePoint(
    unprojectStagePointToSource(
      croppedPoint,
      createImageEditTransform(sourceDimensions, edit),
    ),
    'sourcePoint',
  )
}

export const getOrientedImageDimensions = (
  sourceDimensions: ImageDimensions,
  rotation: ImageRotation,
): ImageDimensions => {
  assertImageDimensions(sourceDimensions, 'sourceDimensions')

  return rotation === 90 || rotation === 270
    ? {
        width: sourceDimensions.height,
        height: sourceDimensions.width,
      }
    : { ...sourceDimensions }
}

export const moveNormalizedCrop = (
  crop: NormalizedCrop,
  deltaX: number,
  deltaY: number,
): NormalizedCrop => {
  assertNormalizedCrop(crop)
  assertFiniteNumber(deltaX, 'deltaX')
  assertFiniteNumber(deltaY, 'deltaY')

  return {
    ...crop,
    x: clamp(crop.x + deltaX, 0, 1 - crop.width),
    y: clamp(crop.y + deltaY, 0, 1 - crop.height),
  }
}

export const centerNormalizedCrop = (crop: NormalizedCrop): NormalizedCrop => {
  assertNormalizedCrop(crop)

  return {
    ...crop,
    x: (1 - crop.width) / 2,
    y: (1 - crop.height) / 2,
  }
}

export const getPixelCropGeometry = (
  crop: NormalizedCrop,
  orientedImageDimensions: ImageDimensions,
): PixelCropGeometry => {
  assertNormalizedCrop(crop)
  assertImageDimensions(orientedImageDimensions, 'orientedImageDimensions')

  const imageWidth = Math.max(1, Math.round(orientedImageDimensions.width))
  const imageHeight = Math.max(1, Math.round(orientedImageDimensions.height))
  const width = clamp(roundPixelValue(crop.width * imageWidth), 1, imageWidth)
  const height = clamp(
    roundPixelValue(crop.height * imageHeight),
    1,
    imageHeight,
  )
  const left = clamp(
    roundPixelValue(crop.x * imageWidth),
    0,
    imageWidth - width,
  )
  const top = clamp(
    roundPixelValue(crop.y * imageHeight),
    0,
    imageHeight - height,
  )

  return {
    left,
    top,
    width,
    height,
  }
}

export const resizeNormalizedCrop = (
  crop: NormalizedCrop,
  handle: CropHandle,
  deltaX: number,
  deltaY: number,
  options: ResizeCropOptions = {},
): NormalizedCrop => {
  assertNormalizedCrop(crop)
  assertFiniteNumber(deltaX, 'deltaX')
  assertFiniteNumber(deltaY, 'deltaY')

  const minimumWidth = getMinimumCropSize(
    options.minimumSize?.width,
    'minimumSize.width',
  )
  const minimumHeight = getMinimumCropSize(
    options.minimumSize?.height,
    'minimumSize.height',
  )

  const horizontalDirection = horizontalDirectionForHandle(handle)
  const verticalDirection = verticalDirectionForHandle(handle)

  if (options.aspectRatio) {
    return resizeCropWithAspectRatio(
      crop,
      horizontalDirection,
      verticalDirection,
      deltaX,
      deltaY,
      minimumWidth,
      minimumHeight,
      options.aspectRatio,
    )
  }

  const resizedCrop = resizeCropWithoutAspectRatio(
    crop,
    horizontalDirection,
    verticalDirection,
    deltaX,
    deltaY,
    minimumWidth,
    minimumHeight,
  )
  const aspectRatioRange = options.aspectRatioRange
  if (!aspectRatioRange) return resizedCrop

  assertPositiveNumber(aspectRatioRange.min, 'aspectRatioRange.min')
  assertPositiveNumber(aspectRatioRange.max, 'aspectRatioRange.max')
  assertImageDimensions(
    aspectRatioRange.imageDimensions,
    'aspectRatioRange.imageDimensions',
  )
  if (aspectRatioRange.min > aspectRatioRange.max) {
    throw new RangeError(
      'aspectRatioRange.min must not exceed aspectRatioRange.max',
    )
  }

  const resizedAspectRatio =
    (resizedCrop.width * aspectRatioRange.imageDimensions.width) /
    (resizedCrop.height * aspectRatioRange.imageDimensions.height)
  const constrainedAspectRatio = clamp(
    resizedAspectRatio,
    aspectRatioRange.min,
    aspectRatioRange.max,
  )
  if (areClose(resizedAspectRatio, constrainedAspectRatio)) return resizedCrop

  return resizeCropWithAspectRatio(
    crop,
    horizontalDirection,
    verticalDirection,
    deltaX,
    deltaY,
    minimumWidth,
    minimumHeight,
    {
      ratio: constrainedAspectRatio,
      imageDimensions: aspectRatioRange.imageDimensions,
    },
  )
}

export const fitCropToAspectRatio = (
  crop: NormalizedCrop,
  aspectRatio: number,
  orientedImageDimensions: ImageDimensions,
): NormalizedCrop => {
  assertNormalizedCrop(crop)
  assertPositiveNumber(aspectRatio, 'aspectRatio')
  assertImageDimensions(orientedImageDimensions, 'orientedImageDimensions')

  const normalizedRatio =
    (aspectRatio * orientedImageDimensions.height) /
    orientedImageDimensions.width
  const currentRatio = crop.width / crop.height

  if (areClose(currentRatio, normalizedRatio)) return { ...crop }

  if (currentRatio > normalizedRatio) {
    const width = crop.height * normalizedRatio

    return {
      ...crop,
      x: crop.x + (crop.width - width) / 2,
      width,
    }
  }

  const height = crop.width / normalizedRatio

  return {
    ...crop,
    y: crop.y + (crop.height - height) / 2,
    height,
  }
}

export const snapNormalizedCropToPixels = (
  crop: NormalizedCrop,
  orientedImageDimensions: ImageDimensions,
): NormalizedCrop => {
  const imageWidth = Math.max(1, Math.round(orientedImageDimensions.width))
  const imageHeight = Math.max(1, Math.round(orientedImageDimensions.height))
  const geometry = getPixelCropGeometry(crop, {
    width: imageWidth,
    height: imageHeight,
  })

  return {
    x: geometry.left / imageWidth,
    y: geometry.top / imageHeight,
    width: geometry.width / imageWidth,
    height: geometry.height / imageHeight,
  }
}

export const rotateImageEditClockwise = (edit: ImageEdit): ImageEdit => ({
  ...edit,
  crop: {
    x: 1 - edit.crop.y - edit.crop.height,
    y: edit.crop.x,
    width: edit.crop.height,
    height: edit.crop.width,
  },
  rotation: getClockwiseRotation(edit.rotation),
  flipHorizontal: edit.flipVertical,
  flipVertical: edit.flipHorizontal,
})

export const rotateImageEditCounterclockwise = (
  edit: ImageEdit,
): ImageEdit => ({
  ...edit,
  crop: {
    x: edit.crop.y,
    y: 1 - edit.crop.x - edit.crop.width,
    width: edit.crop.height,
    height: edit.crop.width,
  },
  rotation: getCounterclockwiseRotation(edit.rotation),
  flipHorizontal: edit.flipVertical,
  flipVertical: edit.flipHorizontal,
})

export const flipImageEditHorizontally = (edit: ImageEdit): ImageEdit => ({
  ...edit,
  crop: {
    ...edit.crop,
    x: 1 - edit.crop.x - edit.crop.width,
  },
  flipHorizontal: !edit.flipHorizontal,
})

export const flipImageEditVertically = (edit: ImageEdit): ImageEdit => ({
  ...edit,
  crop: {
    ...edit.crop,
    y: 1 - edit.crop.y - edit.crop.height,
  },
  flipVertical: !edit.flipVertical,
})

export const getCroppedImageDimensions = (
  sourceDimensions: ImageDimensions,
  edit: ImageEdit,
): ImageDimensions => {
  assertNormalizedCrop(edit.crop)

  const orientedDimensions = getOrientedImageDimensions(
    sourceDimensions,
    edit.rotation,
  )
  const geometry = getPixelCropGeometry(edit.crop, orientedDimensions)

  return {
    width: geometry.width,
    height: geometry.height,
  }
}

export const containImageDimensions = (
  imageDimensions: ImageDimensions,
  bounds: ImageDimensions,
): ImageDimensions => {
  assertImageDimensions(imageDimensions, 'imageDimensions')
  assertImageDimensions(bounds, 'bounds')

  const scale = Math.min(
    bounds.width / imageDimensions.width,
    bounds.height / imageDimensions.height,
  )

  return {
    width: imageDimensions.width * scale,
    height: imageDimensions.height * scale,
  }
}
